/**
 * BackLink — canonical "Back to X" affordance.
 *
 * ONE treatment. No variants. A component with one rendering cannot be
 * applied inconsistently. (Operator ruling 2026-08-08: "we don't need the
 * variant for now; we can always re-introduce one if determined as needed.")
 *
 * DetailPageHeader.backHref + backLabel is still the CANONICAL place to render
 * a back-link on a real entity-detail page. Use this atom ONLY when:
 *   - The surface is NOT an entity detail (no title, no actions)
 *   - You want an inline recovery link (error states, forms)
 *   - You have no other page-header component to hang the link on
 *
 * Copy rule: `Back to <Sentence-Case-Target>` — matches the sidebar target name.
 *   ✅ "Back to Customers"  "Back to Jobs"  "Back to Help Center"
 *   ❌ "Back to customer"   "← Back"        "back to help"
 *
 * @module components/ui/BackLink
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';

export default function BackLink({ to, label, state, className = '' }) {
  const style = { color: 'var(--color-text-muted)' };

  const content = (
    <>
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
      <span>{label}</span>
    </>
  );

  const baseClass = 'inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity';
  const combined = className ? `${baseClass} ${className}` : baseClass;

  if (to) {
    return (
      <Link to={to} state={state} className={combined} style={style}>
        {content}
      </Link>
    );
  }

  // Fallback: no `to` prop. Render as button-styled span for onClick consumers.
  return (
    <span className={combined} style={style}>
      {content}
    </span>
  );
}
